import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import {
  SubcontractReceipt,
  CreateSubcontractReceiptRequest,
  UpdateSubcontractReceiptRequest,
  ApproveQCRequest,
  SubcontractReceiptFilters,
} from '@/types/subcontracting';
import {
  subcontractingEvents,
  createSubcontractingEvent,
  SubcontractReceivedEvent,
} from '@/lib/events/subcontractingEvents';

/**
 * Hook for managing Subcontract Receipts (GRN for subcontracted materials)
 * Handles receipt creation, QC approval, and workflow state management
 */
export const useSubcontractReceipts = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Fetch all subcontract receipts with optional filters
   */
  const fetchSubcontractReceipts = useCallback(async (filters?: SubcontractReceiptFilters) => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('subcontract_receipts_with_details')
        .select('*')
        .order('receipt_date', { ascending: false });

      // Apply filters
      if (filters?.vendor_id) {
        // Need to join with sub_contracts to filter by vendor
        query = query.eq('vendor_id', filters.vendor_id);
      }
      if (filters?.sub_contract_id) {
        query = query.eq('sub_contract_id', filters.sub_contract_id);
      }
      if (filters?.dc_id) {
        query = query.eq('dc_id', filters.dc_id);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.date_from) {
        query = query.gte('receipt_date', filters.date_from);
      }
      if (filters?.date_to) {
        query = query.lte('receipt_date', filters.date_to);
      }
      if (filters?.qc_required !== undefined) {
        query = query.eq('qc_required', filters.qc_required);
      }
      if (filters?.search_term) {
        query = query.or(`receipt_number.ilike.%${filters.search_term}%,dc_number.ilike.%${filters.search_term}%,sc_number.ilike.%${filters.search_term}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      return data as SubcontractReceipt[];
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to fetch subcontract receipts: ${err.message}`,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Fetch a single subcontract receipt by ID with lines
   */
  const fetchSubcontractReceiptById = useCallback(async (receipt_id: string) => {
    setLoading(true);
    setError(null);

    try {
      // Fetch receipt header
      const { data: receiptData, error: receiptError } = await supabase
        .from('subcontract_receipts_with_details')
        .select('*')
        .eq('id', receipt_id)
        .single();

      if (receiptError) throw receiptError;

      // Fetch receipt lines
      const { data: linesData, error: linesError } = await supabase
        .from('subcontract_receipt_lines')
        .select('*')
        .eq('receipt_id', receipt_id)
        .order('created_at', { ascending: true });

      if (linesError) throw linesError;

      return {
        ...receiptData,
        lines: linesData,
      } as SubcontractReceipt;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to fetch subcontract receipt: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Generate next receipt number
   */
  const generateReceiptNumber = useCallback(async (): Promise<string | null> => {
    try {
      const { data, error } = await supabase.rpc('generate_receipt_number');

      if (error) throw error;

      return data as string;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to generate receipt number: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  /**
   * Create a new subcontract receipt
   */
  const createSubcontractReceipt = useCallback(async (request: CreateSubcontractReceiptRequest): Promise<SubcontractReceipt | null> => {
    setLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Generate receipt number
      const receipt_number = await generateReceiptNumber();
      if (!receipt_number) throw new Error('Failed to generate receipt number');

      // Create receipt header
      const { data: receiptData, error: receiptError } = await supabase
        .from('subcontract_receipts')
        .insert({
          receipt_number,
          dc_id: request.dc_id,
          sub_contract_id: request.sub_contract_id || null, // Convert empty string to null for standalone DCs
          received_by: user.id,
          qc_required: request.qc_required,
          qc_template_id: request.qc_template_id || null, // QC template for inspection
          notes: request.notes,
          status: request.qc_required ? 'QC Pending' : 'Pending',
          receiving_warehouse_id: request.receiving_warehouse_id || null,
          inspection_date: request.inspection_date || null,
          inspector_name: request.inspector_name || null,
        })
        .select()
        .single();

      if (receiptError) throw receiptError;

      // Create receipt lines
      if (request.lines && request.lines.length > 0) {
        const linesData = request.lines.map(line => ({
          receipt_id: receiptData.id,
          dc_line_id: line.dc_line_id,
          sub_contract_line_id: line.sub_contract_line_id || null,
          item_code: line.item_code,
          item_description: line.item_description,
          operation_name: line.operation_name,
          qty_sent: line.qty_sent,
          qty_received_good: line.qty_received_good,
          qty_received_reject: line.qty_received_reject,
          uom: line.uom,
          qc_status: line.qc_status || (request.qc_required ? 'Pending' : 'Not Required'),
          qc_remarks: line.qc_remarks,
          rejection_reason: line.rejection_reason,
          rejection_remarks: line.rejection_remarks,
          line_notes: line.line_notes,
          lot_number: line.lot_number || null,
          bin_location: line.bin_location || null,
        }));

        const { error: linesError } = await supabase
          .from('subcontract_receipt_lines')
          .insert(linesData);

        if (linesError) throw linesError;

        // Update DC line qty_received for partial receipt tracking
        for (const line of request.lines) {
          const totalReceived = line.qty_received_good + line.qty_received_reject;

          // Get current qty_received for this DC line
          const { data: dcLineData } = await supabase
            .from('delivery_challan_lines')
            .select('qty_received, qty_sent')
            .eq('id', line.dc_line_id)
            .single();

          if (dcLineData) {
            const newQtyReceived = (dcLineData.qty_received || 0) + totalReceived;

            await supabase
              .from('delivery_challan_lines')
              .update({ qty_received: newQtyReceived })
              .eq('id', line.dc_line_id);
          }
        }
      }

      // Calculate DC receipt status based on all lines
      const { data: dcLines } = await supabase
        .from('delivery_challan_lines')
        .select('qty_sent, qty_received')
        .eq('dc_id', request.dc_id);

      const totalSent = dcLines?.reduce((sum, l) => sum + (l.qty_sent || 0), 0) || 0;
      const totalReceived = dcLines?.reduce((sum, l) => sum + (l.qty_received || 0), 0) || 0;

      let receiptStatus: 'Not Received' | 'Partially Received' | 'Fully Received' = 'Not Received';
      if (totalReceived >= totalSent) {
        receiptStatus = 'Fully Received';
      } else if (totalReceived > 0) {
        receiptStatus = 'Partially Received';
      }

      // Update DC status based on receipt progress
      const { error: dcError } = await supabase
        .from('delivery_challans')
        .update({
          status: receiptStatus === 'Fully Received' ? 'Received' : 'Dispatched',
          receipt_status: receiptStatus,
          actual_return_date: receiptStatus === 'Fully Received' ? new Date().toISOString() : null,
          received_by: receiptStatus === 'Fully Received' ? user.id : null,
        })
        .eq('id', request.dc_id);

      if (dcError) throw dcError;

      // Update subcontract status to Received (only if linked to a subcontract)
      if (request.sub_contract_id) {
        const { error: scError } = await supabase
          .from('sub_contracts')
          .update({
            status: request.qc_required ? 'QC Pending' : 'Received',
            receipt_date: new Date().toISOString(),
          })
          .eq('id', request.sub_contract_id);

        if (scError) throw scError;
      }

      toast({
        title: 'Success',
        description: `Subcontract Receipt ${receipt_number} created successfully`,
      });

      // Fetch and return the complete receipt with lines
      return await fetchSubcontractReceiptById(receiptData.id);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to create subcontract receipt: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast, generateReceiptNumber, fetchSubcontractReceiptById]);

  /**
   * Update subcontract receipt details
   */
  const updateSubcontractReceipt = useCallback(async (
    receipt_id: string,
    request: UpdateSubcontractReceiptRequest
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('subcontract_receipts')
        .update({
          qc_required: request.qc_required,
          qc_remarks: request.qc_remarks,
          notes: request.notes,
        })
        .eq('id', receipt_id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Subcontract Receipt updated successfully',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to update subcontract receipt: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Approve QC for subcontract receipt
   */
  const approveQC = useCallback(async (request: ApproveQCRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Update receipt header
      const { error: receiptError } = await supabase
        .from('subcontract_receipts')
        .update({
          status: 'QC Approved',
          qc_date: new Date().toISOString(),
          qc_by: user.id,
          qc_remarks: request.qc_remarks,
        })
        .eq('id', request.receipt_id);

      if (receiptError) throw receiptError;

      // Update receipt lines if provided
      if (request.line_updates && request.line_updates.length > 0) {
        for (const lineUpdate of request.line_updates) {
          const { error: lineError } = await supabase
            .from('subcontract_receipt_lines')
            .update({
              qc_status: lineUpdate.qc_status,
              qc_remarks: lineUpdate.qc_remarks,
            })
            .eq('id', lineUpdate.line_id);

          if (lineError) throw lineError;
        }
      }

      // Get receipt to update subcontract
      const { data: receiptData } = await supabase
        .from('subcontract_receipts')
        .select('sub_contract_id')
        .eq('id', request.receipt_id)
        .single();

      if (receiptData?.sub_contract_id) {
        const { error: scError } = await supabase
          .from('sub_contracts')
          .update({
            status: 'QC Approved',
            qc_date: new Date().toISOString(),
          })
          .eq('id', receiptData.sub_contract_id);

        if (scError) throw scError;
      }

      toast({
        title: 'Success',
        description: 'QC approved successfully',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to approve QC: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);


  /**
   * Reject QC for subcontract receipt
   */
  const rejectQC = useCallback(async (receipt_id: string, qc_remarks: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Update receipt header
      const { error: receiptError } = await supabase
        .from('subcontract_receipts')
        .update({
          status: 'QC Rejected',
          qc_date: new Date().toISOString(),
          qc_by: user.id,
          qc_remarks,
        })
        .eq('id', receipt_id);

      if (receiptError) throw receiptError;

      // Get receipt to update subcontract
      const { data: receiptData } = await supabase
        .from('subcontract_receipts')
        .select('sub_contract_id')
        .eq('id', receipt_id)
        .single();

      if (receiptData?.sub_contract_id) {
        const { error: scError } = await supabase
          .from('sub_contracts')
          .update({
            status: 'QC Rejected',
            qc_date: new Date().toISOString(),
          })
          .eq('id', receiptData.sub_contract_id);

        if (scError) throw scError;
      }

      toast({
        title: 'Success',
        description: 'QC rejected',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to reject QC: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Complete subcontract receipt (mark as Completed)
   * Creates inventory transactions for ALL receipt types (standalone DC, subcontract DC, work order linked)
   * Also updates linked work order operation status to 'Completed'
   * Emits subcon.received event to trigger additional integrations
   */
  const completeReceipt = useCallback(async (receipt_id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Get current user for transaction tracking
      const { data: { user } } = await supabase.auth.getUser();

      // Update receipt status
      const { error: receiptError } = await supabase
        .from('subcontract_receipts')
        .update({ status: 'Completed' })
        .eq('id', receipt_id);

      if (receiptError) throw receiptError;

      // Get receipt with lines and DC details for inventory transactions
      const { data: receiptData, error: fetchError } = await supabase
        .from('subcontract_receipts')
        .select(`
          *,
          lines:subcontract_receipt_lines(*),
          delivery_challan:dc_id(id, dc_number, vendor_id, vendor_name)
        `)
        .eq('id', receipt_id)
        .single();

      if (fetchError) {
        console.error('Error fetching receipt:', fetchError);
        throw new Error(`Failed to fetch receipt: ${fetchError.message}`);
      }

      if (!receiptData) {
        throw new Error('Receipt not found');
      }

      // Get warehouse IDs for inventory transactions
      const { data: warehouses } = await supabase
        .from('warehouse')
        .select('id, code')
        .in('code', ['SC-WIP', 'MAIN', 'WIP-INHOUSE']);

      const warehouseMap: Record<string, string> = {};
      (warehouses || []).forEach((wh: any) => {
        warehouseMap[wh.code] = wh.id;
      });

      const scWipWarehouseId = warehouseMap['SC-WIP'];
      const mainWarehouseId = warehouseMap['MAIN'];
      const wipWarehouseId = warehouseMap['WIP-INHOUSE'];

      // Determine receiving warehouse (use receipt's warehouse or default to MAIN)
      const receivingWarehouseId = receiptData.receiving_warehouse_id || mainWarehouseId;

      // ============================================
      // CREATE INVENTORY TRANSACTIONS FOR ALL RECEIPTS
      // ============================================
      const inventoryTransactions: any[] = [];
      const receiptLines = receiptData.lines || [];

      for (const line of receiptLines) {
        // Skip lines with no good quantity
        if (!line.qty_received_good || line.qty_received_good <= 0) continue;

        // Look up inventory item by code (inventory_items uses 'code' not 'item_code')
        const { data: invItem } = await supabase
          .from('inventory_items')
          .select('id')
          .eq('code', line.item_code)
          .single();

        const inventoryItemId = invItem?.id || null;

        // Create SUBCON-RECEIPT transaction for good quantity
        inventoryTransactions.push({
          txn_type: 'SUBCON-RECEIPT',
          inventory_item_id: inventoryItemId,
          item_id: inventoryItemId, // Legacy field
          qty: line.qty_received_good,
          uom: line.uom || 'pcs',
          wh_from: scWipWarehouseId,
          wh_to: receivingWarehouseId,
          ref_table: 'subcontract_receipts',
          ref_id: receipt_id,
          note: `Subcontract receipt: ${receiptData.receipt_number} - Item: ${line.item_code} (${line.item_description || ''})`,
          created_by: user?.id,
          created_at: new Date().toISOString(),
        });

        // Create SUBCON-REJECT transaction for rejected quantity (if any)
        if (line.qty_received_reject && line.qty_received_reject > 0) {
          // Get scrap warehouse
          const { data: scrapWh } = await supabase
            .from('warehouse')
            .select('id')
            .eq('code', 'SCRAP')
            .single();

          inventoryTransactions.push({
            txn_type: 'SUBCON-REJECT',
            inventory_item_id: inventoryItemId,
            item_id: inventoryItemId,
            qty: line.qty_received_reject,
            uom: line.uom || 'pcs',
            wh_from: scWipWarehouseId,
            wh_to: scrapWh?.id || receivingWarehouseId,
            ref_table: 'subcontract_receipts',
            ref_id: receipt_id,
            note: `Subcontract rejection: ${receiptData.receipt_number} - Item: ${line.item_code} (QC Rejected)`,
            created_by: user?.id,
            created_at: new Date().toISOString(),
          });
        }
      }

      // Insert all inventory transactions
      if (inventoryTransactions.length > 0) {
        const { error: txError } = await supabase
          .from('inventory_tx')
          .insert(inventoryTransactions);

        if (txError) {
          console.error('Failed to create inventory transactions:', txError);
          // Don't throw - continue with completion even if inventory fails
        } else {
          console.log(`✅ Created ${inventoryTransactions.length} inventory transaction(s) for receipt ${receiptData.receipt_number}`);
        }
      }

      // Variables for event emission
      let scData: any = null;
      let operationIds: string[] = [];
      let jobCardIds: string[] = [];
      let vendorName = 'Unknown Vendor';

      // Get vendor name from DC if available
      if (receiptData.delivery_challan) {
        const dc = receiptData.delivery_challan as any;
        vendorName = dc.vendor_name || 'Unknown Vendor';
      }

      // Process subcontract-related updates if linked to a subcontract
      if (receiptData.sub_contract_id) {
        // Get subcontract details for event
        const { data: subcontractData } = await supabase
          .from('sub_contracts')
          .select('id, sc_number, vendor_id, vendor_name')
          .eq('id', receiptData.sub_contract_id)
          .single();
        
        scData = subcontractData;
        if (scData?.vendor_name) {
          vendorName = scData.vendor_name || vendorName;
        }

        // Update subcontract status
        const { error: scError } = await supabase
          .from('sub_contracts')
          .update({
            status: 'Completed',
            completion_date: new Date().toISOString(),
          })
          .eq('id', receiptData.sub_contract_id);

        if (scError) throw scError;

        // Get linked work order operations and job cards from sub_contract_lines
        const { data: scLines } = await supabase
          .from('sub_contract_lines')
          .select('routing_operation_id, job_card_id')
          .eq('sub_contract_id', receiptData.sub_contract_id);

        operationIds = (scLines || [])
          .map(line => line.routing_operation_id)
          .filter((id): id is string => id !== null);

        jobCardIds = (scLines || [])
          .map(line => line.job_card_id)
          .filter((id): id is string => id !== null);

        if (operationIds.length > 0) {
          // Update work order operations status to Completed
          const { error: woOpError } = await supabase
            .from('work_order_operations')
            .update({
              status: 'Completed',
              actual_end_time: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .in('id', operationIds);

          if (woOpError) {
            console.error('Failed to update work order operations:', woOpError);
          } else {
            console.log(`✅ Updated ${operationIds.length} work order operation(s) to Completed`);
          }
        }

        // Emit subcon.received event for additional integrations (costing, scheduling)
        if (scData) {
          const receivedQuantities = receiptLines.map((line: any) => ({
            jobCardId: line.job_card_id || '',
            qtyGood: line.qty_received_good || 0,
            qtyReject: line.qty_received_reject || 0,
          }));

          const completedItems = receiptLines.map((line: any) => ({
            itemCode: line.item_code || '',
            quantity: line.qty_received_good || 0,
            uom: line.uom || 'pcs',
          }));

          const event = createSubcontractingEvent<SubcontractReceivedEvent>(
            'subcon.received',
            scData.id,
            scData.sc_number,
            scData.vendor_id,
            vendorName,
            {
              jobCardIds,
              operationIds,
              receivedQuantities,
              completedItems,
            }
          );

          // Emit event asynchronously (don't block completion)
          subcontractingEvents.emit(event).then(() => {
            console.log('✅ Emitted subcon.received event for integrations');
          }).catch((err) => {
            console.error('Failed to process subcon.received event:', err);
          });
        }
      } else {
        // Standalone DC receipt - inventory already created above
        console.log(`✅ Standalone DC receipt completed with ${inventoryTransactions.length} inventory transaction(s)`);
      }

      toast({
        title: 'Success',
        description: `Receipt completed successfully. ${inventoryTransactions.length} inventory transaction(s) created.`,
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to complete receipt: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    loading,
    error,
    fetchSubcontractReceipts,
    fetchSubcontractReceiptById,
    generateReceiptNumber,
    createSubcontractReceipt,
    updateSubcontractReceipt,
    approveQC,
    rejectQC,
    completeReceipt,
  };
};
